import React, { useMemo, useState } from "react";
import { Localization } from "@viamap/viamap2-common";
import { ADModalBody, ADModalFooter, AdvancedDragModal } from "src/componentsUtils/AdvancedDragModal";
import { GlassButton, GlassButtonRow } from "src/components/GlassButtons";
import { GlassInputControl, GlassInputGroup, GlassTextInputTransformer } from "src/components/GlassInput";
import { MIBIcon } from "src/componentsUtils/MIBIcon";
import { LayerInfo, LayerType } from "src/common/managers/Types";
import { PLAElementRecord, PLAProject, PLARelationRecord, AttributeValue, PLAValueType, PLAAttributeDefinition } from "./PLAState";
import { explainPLAExpression, PLAExpressionExplanation, resolvePLACalculatedAttributes } from "./PLAExpression";

function defaultValueForType(valueType: PLAValueType | undefined) {
   if (valueType === "number") return "";
   if (valueType === "boolean") return false;
   return "";
}

function copyGeometryFromLayer(layer: LayerInfo, element: PLAElementRecord): PLAElementRecord {
   const feature = layer.geoJson?.features?.[0] || layer.geoJson;
   const geometry = feature?.geometry || feature;
   if (!geometry) return element;
   if (geometry.type === "Point") {
      return { ...element, geoPoint: geometry.coordinates };
   }
   if (geometry.type === "Polygon" || geometry.type === "MultiPolygon") {
      return { ...element, geoShape: geometry };
   }
   return element;
}

export function shouldShowPLAElementAttribute(definition: PLAAttributeDefinition, isNewElement: boolean) {
   return definition.type === "constant" || !isNewElement;
}

export function createEmptyPLAElement(project: PLAProject, elementTypeId: string): PLAElementRecord {
   const definitions = project.elementTypes[elementTypeId]?.attributes || {};
   const attributes = Object.entries(definitions).reduce<Record<string, AttributeValue>>((acc, [attributeId, definition]) => {
      if (definition.type === "constant") {
         acc[attributeId] = { mode: "constant", valueType: definition.valueType || "string", value: defaultValueForType(definition.valueType) };
      }
      return acc;
   }, {});
   return {
      recordType: "ELEMENT",
      id: crypto.randomUUID(),
      elementType: elementTypeId,
      name: project.elementTypes[elementTypeId]?.name || elementTypeId,
      attributes
   };
}

export function PLAElementDialog(props: {
   project: PLAProject;
   element: PLAElementRecord;
   elements: PLAElementRecord[];
   relations: PLARelationRecord[];
   availableLayers: LayerInfo[];
   readOnly: boolean;
   onClose: () => void;
   onSave: (element: PLAElementRecord) => void;
   onCreateRelation: (element: PLAElementRecord) => void;
   onDeleteRelation: (relationId: string) => void;
}) {
   const [element, setElement] = useState<PLAElementRecord>(props.element);
   const [explanation, setExplanation] = useState<PLAExpressionExplanation | { expression: string; error: string } | undefined>();
   const definitions = props.project.elementTypes[element.elementType]?.attributes || {};
   const calculated = useMemo(() => {
      return resolvePLACalculatedAttributes(element, props.project, props.elements, props.relations, { includeErrors: true });
   }, [element, props.project, props.elements, props.relations]);
   const currentRelations = props.relations.filter((relation) => relation.fromId === element.id || relation.toId === element.id);
   const isNewElement = !props.elements.some((item) => item.id === element.id);
   const editableDefinitions = Object.entries(definitions).filter(([, definition]) => shouldShowPLAElementAttribute(definition, isNewElement));

   const setConstantAttribute = (attributeId: string, value: any) => {
      const definition = definitions[attributeId];
      setElement((prev) => ({
         ...prev,
         attributes: {
            ...prev.attributes,
            [attributeId]: { mode: "constant", valueType: definition.valueType || "string", value }
         }
      }));
   };
   const setConstantAttributeFromInput = (attributeId: string, value: string) => {
      const definition = definitions[attributeId];
      setConstantAttribute(attributeId, definition.valueType === "number" ? (value === "" ? "" : Number(value)) : value);
   };

   const geometryStatus = (present: boolean) => present ? Localization.getText("Present") : Localization.getText("Not present");
   const openExpressionExplanation = (expression: string) => {
      try {
         setExplanation(explainPLAExpression(expression || "0", { element, elements: props.elements, relations: props.relations, project: props.project, calculated }));
      } catch (error: any) {
         setExplanation({ expression, error: error.message || String(error) });
      }
   };

   return (
      <AdvancedDragModal title={Localization.getText("Element editor")} PosDefault={{ width: "640px", left: "50%", transform: "translateX(-50%)", top: "80px" }} onClose={props.onClose}>
         <ADModalBody>
            <GlassInputGroup onSubmit={() => props.onSave(element)} onEscape={props.onClose}>
               <GlassTextInputTransformer label={Localization.getText("Name")}>
                  <input disabled={props.readOnly} value={element.name} onChange={(event) => setElement({ ...element, name: event.target.value })} />
               </GlassTextInputTransformer>
               {!props.readOnly && <GlassTextInputTransformer label={Localization.getText("Geometry from layer")}>
                  <select value="" onChange={(event) => {
                     const layer = props.availableLayers.find((item) => String(item.layerId) === event.target.value);
                     if (layer) setElement(copyGeometryFromLayer(layer, element));
                  }}>
                     <option value="">{Localization.getText("Select a data layer")}</option>
                     {props.availableLayers.filter((layer) => [LayerType.GeoJSON_Point, LayerType.GeoJSON_Polygon, LayerType.GeoJSON].includes(layer.type)).map((layer) => (
                        <option key={layer.layerId} value={layer.layerId}>{layer.datasetname || layer.filename || layer.layerId}</option>
                     ))}
                  </select>
               </GlassTextInputTransformer>}
               <GlassTextInputTransformer label={Localization.getText("Geo point")}>
                  <span>{geometryStatus(Boolean(element.geoPoint))}</span>
               </GlassTextInputTransformer>
               <GlassTextInputTransformer label={Localization.getText("Geo shape")}>
                  <span>{geometryStatus(Boolean(element.geoShape))}</span>
               </GlassTextInputTransformer>
               {editableDefinitions.map(([attributeId, definition]) => (
                  <GlassTextInputTransformer key={attributeId} label={definition.name || attributeId}>
                     <GlassInputControl>
                        {definition.type === "constant" ? (
                           <input
                              disabled={props.readOnly}
                              type={definition.valueType === "number" ? "number" : definition.valueType === "date" ? "date" : "text"}
                              value={(element.attributes[attributeId] as any)?.value ?? ""}
                              onChange={(event) => setConstantAttributeFromInput(attributeId, event.target.value)}
                           />
                        ) : (
                           <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                              <span>{String(calculated[attributeId] ?? "")}</span>
                              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                 <small style={{ flex: 1 }}>{definition.expression}</small>
                                 <GlassButton onClick={() => openExpressionExplanation(definition.expression || "0")}>{Localization.getText("Explain")}</GlassButton>
                              </div>
                           </div>
                        )}
                     </GlassInputControl>
                  </GlassTextInputTransformer>
               ))}
            </GlassInputGroup>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
               <h4 style={{ margin: 0 }}>{Localization.getText("Relations")}</h4>
               {!props.readOnly && (
                  <GlassButton
                     title={Localization.getText("Create relation")}
                     aria-label={Localization.getText("Create relation")}
                     onClick={() => props.onCreateRelation(element)}
                     style={{ width: 30, minWidth: 30, height: 28, padding: 0, display: "inline-flex", alignItems: "center", justifyContent: "center" }}
                  >
                     <MIBIcon icon="mapLink" />
                  </GlassButton>
               )}
            </div>
            <table style={{ width: "100%" }}>
               <tbody>
                  {currentRelations.map((relation) => (
                     <tr key={relation.id}>
                        <td>{props.project.relationTypes[relation.relationType]?.name || relation.relationType}</td>
                        <td>{relation.fromId === element.id ? Localization.getText("Outgoing") : Localization.getText("Incoming")}</td>
                        <td>{props.elements.find((item) => item.id === (relation.fromId === element.id ? relation.toId : relation.fromId))?.name}</td>
                        <td>{!props.readOnly && (
                           <GlassButton
                              title={Localization.getText("Delete relation")}
                              aria-label={Localization.getText("Delete relation")}
                              onClick={() => props.onDeleteRelation(relation.id)}
                              style={{ width: 30, minWidth: 30, height: 28, padding: 0, display: "inline-flex", alignItems: "center", justifyContent: "center" }}
                           >
                              <MIBIcon icon="delete" />
                           </GlassButton>
                        )}</td>
                     </tr>
                  ))}
               </tbody>
            </table>
         </ADModalBody>
         <ADModalFooter>
            <GlassButtonRow>
               {!props.readOnly && <GlassButton onClick={() => props.onSave(element)}>{Localization.getText("Save")}</GlassButton>}
               <GlassButton onClick={props.onClose}>{Localization.getText("Close")}</GlassButton>
            </GlassButtonRow>
         </ADModalFooter>
         {explanation && (
            <AdvancedDragModal title={Localization.getText("Expression explanation")} PosDefault={{ width: "720px", left: "50%", transform: "translateX(-50%)", top: "120px" }} onClose={() => setExplanation(undefined)}>
               <ADModalBody>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                     <strong>{explanation.expression}</strong>
                     {"error" in explanation ? (
                        <pre style={{ whiteSpace: "pre-wrap", margin: 0 }}>{explanation.error}</pre>
                     ) : (
                        <pre style={{ whiteSpace: "pre-wrap", margin: 0 }}>{explanation.lines.join("\n")}</pre>
                     )}
                  </div>
               </ADModalBody>
               <ADModalFooter>
                  <GlassButtonRow>
                     <GlassButton onClick={() => setExplanation(undefined)}>{Localization.getText("Close")}</GlassButton>
                  </GlassButtonRow>
               </ADModalFooter>
            </AdvancedDragModal>
         )}
      </AdvancedDragModal>
   );
}

export function PLARelationDialog(props: {
   project: PLAProject;
   sourceElement: PLAElementRecord;
   elements: PLAElementRecord[];
   onClose: () => void;
   onSave: (relation: PLARelationRecord) => void;
}) {
   const [relationType, setRelationType] = useState(Object.keys(props.project.relationTypes)[0] || "");
   const targetElements = props.elements.filter((element) => element.id !== props.sourceElement.id);
   const firstTargetElement = targetElements[0];
   const [targetElementTypeId, setTargetElementTypeId] = useState(firstTargetElement?.elementType || "");
   const visibleTargetElements = targetElements.filter((element) => element.elementType === targetElementTypeId);
   const [targetElementId, setTargetElementId] = useState(visibleTargetElements[0]?.id || firstTargetElement?.id || "");
   const elementTypeOptions = Object.entries(props.project.elementTypes).filter(([elementTypeId]) => targetElements.some((element) => element.elementType === elementTypeId));
   const saveRelation = () => props.onSave({ recordType: "RELATION", id: crypto.randomUUID(), relationType, fromId: props.sourceElement.id, toId: targetElementId });

   return (
      <AdvancedDragModal title={Localization.getText("Create relation")} PosDefault={{ width: "440px", left: "50%", transform: "translateX(-50%)", top: "120px" }} onClose={props.onClose}>
         <ADModalBody>
            <GlassInputGroup onSubmit={saveRelation} onEscape={props.onClose}>
               <GlassTextInputTransformer label={Localization.getText("Relation type")}>
                  <select value={relationType} onChange={(event) => setRelationType(event.target.value)}>
                     {Object.entries(props.project.relationTypes).map(([id, relation]) => <option key={id} value={id}>{relation.name || id}</option>)}
                  </select>
               </GlassTextInputTransformer>
               <GlassTextInputTransformer label={Localization.getText("Target element type")}>
                  <select value={targetElementTypeId} onChange={(event) => {
                     const nextElementTypeId = event.target.value;
                     const nextTarget = targetElements.find((element) => element.elementType === nextElementTypeId);
                     setTargetElementTypeId(nextElementTypeId);
                     setTargetElementId(nextTarget?.id || "");
                  }}>
                     {elementTypeOptions.map(([id, elementType]) => <option key={id} value={id}>{elementType.name || id}</option>)}
                  </select>
               </GlassTextInputTransformer>
               <GlassTextInputTransformer label={Localization.getText("Target element")}>
                  <select value={targetElementId} onChange={(event) => setTargetElementId(event.target.value)}>
                     {visibleTargetElements.map((element) => <option key={element.id} value={element.id}>{element.name}</option>)}
                  </select>
               </GlassTextInputTransformer>
            </GlassInputGroup>
         </ADModalBody>
         <ADModalFooter>
            <GlassButtonRow>
               <GlassButton disabled={!relationType || !targetElementId} onClick={saveRelation}>{Localization.getText("Save")}</GlassButton>
               <GlassButton onClick={props.onClose}>{Localization.getText("Close")}</GlassButton>
            </GlassButtonRow>
         </ADModalFooter>
      </AdvancedDragModal>
   );
}
